import React from 'react';
import { Check } from 'lucide-react';
import type { LongPressRowHandlers } from '../../hooks/useLongPressSelect';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  finalized: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
};

export interface SelectableDocRow {
  id: string;
  title: string;
  subtitle: string;
  status: string;
}

interface SelectableDocumentListProps {
  rows: SelectableDocRow[];
  selecting: boolean;
  selected: Set<string>;
  bindRow: (
    id: string,
    enabled: boolean,
    onOpen?: () => void
  ) => LongPressRowHandlers;
  onOpen: (id: string) => void;
}

export const SelectableDocumentList: React.FC<SelectableDocumentListProps> = ({
  rows,
  selecting,
  selected,
  bindRow,
  onOpen,
}) => {
  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const canSelect = row.status === 'draft';
        const isOn = selected.has(row.id);
        const handlers = bindRow(row.id, canSelect, () => onOpen(row.id));
        return (
          <button
            key={row.id}
            type="button"
            className={`w-full text-left bg-white rounded-2xl border-2 p-4 ${
              isOn ? 'border-red-400 bg-red-50' : 'border-gray-100 hover:border-brand-200'
            } ${selecting && !canSelect ? 'opacity-50' : ''}`}
            {...handlers}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {selecting && canSelect ? (
                  <span
                    className={`mt-0.5 h-6 w-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                      isOn ? 'bg-red-600 border-red-600 text-white' : 'border-gray-300'
                    }`}
                  >
                    {isOn ? <Check size={14} /> : null}
                  </span>
                ) : null}
                <div className="min-w-0">
                  <p className="font-black text-gray-900 truncate">{row.title}</p>
                  <p className="text-xs text-gray-500 truncate">{row.subtitle}</p>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-black uppercase flex-shrink-0 ${
                  STATUS_COLORS[row.status] || ''
                }`}
              >
                {row.status}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
